import clsx from 'clsx';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import TermsCheckboxIcon from '@/components/icons/CheckboxIcon';
import { SuccessState } from '@/components/SuccessState';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TextField } from '@/components/ui/TextField';
import { useCreateFavoriteFilter } from '@/hooks/queries/useFavoriteFiltersQueries';
import { LoanType, PoolFilters, PoolSortOrder, PoolType } from '@/types/pool';
import { formatCurrency, LoanTypeLabel } from '@/utils/misc';
import { POOL_TYPE_DISPLAY } from '@/utils/poolDisplay';
import { ModalTitle } from '../../../ModalTitle';

type ActiveFilter = { label: string; value: string };

function getActiveFilters(filters: PoolFilters): ActiveFilter[] {
  const activeFilters: ActiveFilter[] = [];

  if (filters.min_amount != null) {
    activeFilters.push({ label: 'Min Amount', value: formatCurrency(filters.min_amount) });
  }
  if (filters.max_amount != null) {
    activeFilters.push({ label: 'Max Amount', value: formatCurrency(filters.max_amount) });
  }
  if (filters.min_roi != null) {
    activeFilters.push({ label: 'Min Interest Rate', value: `${filters.min_roi}%` });
  }
  if (filters.max_roi != null) {
    activeFilters.push({ label: 'Max Interest Rate', value: `${filters.max_roi}%` });
  }
  if (filters.min_property_value != null) {
    activeFilters.push({ label: 'Min Property Value', value: formatCurrency(filters.min_property_value) });
  }
  if (filters.max_property_value != null) {
    activeFilters.push({ label: 'Max Property Value', value: formatCurrency(filters.max_property_value) });
  }
  if (filters.pool_type) {
    activeFilters.push({
      label: 'Pool Type',
      value: POOL_TYPE_DISPLAY[filters.pool_type as PoolType] ?? filters.pool_type,
    });
  }
  if (filters.pool_program) {
    activeFilters.push({
      label: 'Pool Program',
      value: LoanTypeLabel[filters.pool_program as LoanType] ?? filters.pool_program,
    });
  }
  if (filters.sort_order) {
    activeFilters.push({ label: 'Sort Order', value: filters.sort_order === PoolSortOrder.ASC ? 'Oldest' : 'Newest' });
  }

  return activeFilters;
}

type FavoriteFilterModalProps = {
  currentFilters: PoolFilters;
  onCreateSuccess?: () => void;
};

type FavoriteFilterModalMode = 'create' | 'create-success' | 'update-success';

export type FavoriteFilterModalHandle = {
  open: () => void;
  openUpdateSuccess: () => void;
  close: () => void;
};

export default forwardRef<FavoriteFilterModalHandle, FavoriteFilterModalProps>(function FavoriteFilterModal(
  { currentFilters, onCreateSuccess }: FavoriteFilterModalProps,
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<FavoriteFilterModalMode>('create');
  const [filterName, setFilterName] = useState('');
  const [notificationsDisabled, setNotificationsDisabled] = useState(false);

  const createFilter = useCreateFavoriteFilter();

  const activeFilters = useMemo(() => getActiveFilters(currentFilters), [currentFilters]);
  const isSaving = createFilter.isPending;
  const activeError = createFilter.error;
  const errorMessage = activeError instanceof Error ? activeError.message : undefined;

  const resetModalState = () => {
    setMode('create');
    setFilterName('');
    setNotificationsDisabled(false);
    createFilter.reset();
  };

  const handleClose = () => {
    setIsOpen(false);
    resetModalState();
  };

  const handleOpen = () => {
    resetModalState();
    setIsOpen(true);
  };

  const handleOpenUpdateSuccess = () => {
    setMode('update-success');
    setIsOpen(true);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    openUpdateSuccess: handleOpenUpdateSuccess,
    close: handleClose,
  }));

  const handleFilterNameChange = (value: string) => {
    setFilterName(value);

    if (createFilter.error) {
      createFilter.reset();
    }
  };

  const handleSave = () => {
    if (!filterName.trim()) return;

    createFilter.mutate(
      {
        name: filterName.trim(),
        filters: currentFilters,
        notifications_enabled: !notificationsDisabled,
      },
      {
        onSuccess: () => {
          onCreateSuccess?.();
          setMode('create-success');
        },
      }
    );
  };

  const successStateTitle = mode === 'update-success' ? 'Filter updated successfully' : 'Filter saved successfully';
  const successStateMessage =
    mode === 'update-success'
      ? 'Your favorite filter was successfully updated.'
      : 'Your favorite filter was saved successfully.';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={500} header={<ModalTitle title={'Save Filter'} />}>
      {mode !== 'create' ? (
        <div className="p-6">
          <SuccessState title={successStateTitle} message={successStateMessage} onButtonClick={handleClose} />
        </div>
      ) : (
        <div className="p-6 flex flex-col gap-5">
          {activeFilters.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-semibold text-black font-avenir">Active Filters</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeFilters.map((f) => (
                  <div
                    key={f.label}
                    className="rounded-xl border border-gray-200 bg-[#FAFAFA] px-3 py-2 flex items-center justify-between gap-2"
                  >
                    <span className="text-[11px] font-medium text-gray-500 font-avenir">{f.label}</span>
                    <span className="text-xs font-semibold text-[#113D7B] font-avenir">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200" />

          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold text-black font-avenir">Filter Name</h4>
            <TextField placeholder="Enter filter name" value={filterName} onChange={handleFilterNameChange} autoFocus />
            {errorMessage && <p className="mt-1 text-[#cc4747] text-xs font-[var(--ep-font-avenir)]">{errorMessage}</p>}
          </div>

          <div className="flex items-start gap-2">
            <button
              className={clsx(
                'w-4 h-4 min-w-[1rem] mt-0.5 border-2 rounded-sm cursor-pointer flex items-center justify-center flex-shrink-0',
                notificationsDisabled ? 'border-blue-600 bg-blue-600' : 'border-gray-400 bg-transparent'
              )}
              onClick={() => setNotificationsDisabled((prev) => !prev)}
            >
              <TermsCheckboxIcon />
            </button>
            <button
              className="text-black text-xs font-normal leading-relaxed text-left min-w-0 cursor-pointer select-none"
              onClick={() => setNotificationsDisabled((prev) => !prev)}
            >
              Turn off notifications for new matching pools.
            </button>
          </div>

          <div className="flex gap-4 justify-end mt-2">
            <Button onClick={handleClose} variant="secondary" disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" disabled={!filterName.trim() || isSaving} loading={isSaving}>
              Save Filter
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
});
